import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface PaymentSettings {
  cartEnabled: boolean;
  multibancoEnabled: boolean;
  mbwayEnabled: boolean;
  cardEnabled: boolean;
  creditTermsEnabled: boolean;
  installmentsEnabled: boolean;
  promptPaymentDiscountEnabled: boolean;
  promptPaymentDiscountPercentage: number;
  promptPaymentDiscountMethods: string[];
  isLoading: boolean;
}

const SETTING_KEYS = [
  "cart_enabled",
  "payment_multibanco_enabled",
  "payment_mbway_enabled",
  "payment_card_enabled",
  "payment_credit_terms_enabled",
  "payment_installments_enabled",
  "prompt_payment_discount_enabled",
  "prompt_payment_discount_percentage",
  "prompt_payment_discount_methods",
];

export function usePaymentSettings(): PaymentSettings {
  const { data, isLoading } = useQuery({
    queryKey: ["payment-settings"],
    queryFn: async () => {
      // Use SECURITY DEFINER RPC — public read of only payment-related settings
      // (app_settings table itself is admin-only via RLS).
      const { data, error } = await (supabase.rpc as any)("get_payment_settings");

      if (error) throw error;

      const settingsMap: Record<string, string> = {};
      (data as Array<{ setting_key: string; setting_value: string }> | null)?.forEach((item) => {
        settingsMap[item.setting_key] = item.setting_value;
      });
      // silence unused warning for legacy constant
      void SETTING_KEYS;

      return {
        cartEnabled: settingsMap.cart_enabled !== "false",
        multibancoEnabled: settingsMap.payment_multibanco_enabled !== "false",
        mbwayEnabled: settingsMap.payment_mbway_enabled !== "false",
        cardEnabled: settingsMap.payment_card_enabled !== "false",
        creditTermsEnabled: settingsMap.payment_credit_terms_enabled !== "false",
        installmentsEnabled: settingsMap.payment_installments_enabled !== "false",
        promptPaymentDiscountEnabled: settingsMap.prompt_payment_discount_enabled === "true",
        promptPaymentDiscountPercentage: parseFloat(settingsMap.prompt_payment_discount_percentage || "2"),
        promptPaymentDiscountMethods: (settingsMap.prompt_payment_discount_methods || "multibanco,mbway").split(",").map(m => m.trim()),
      };
    },
    staleTime: 60 * 1000,
  });

  return {
    cartEnabled: data?.cartEnabled ?? true,
    multibancoEnabled: data?.multibancoEnabled ?? true,
    mbwayEnabled: data?.mbwayEnabled ?? true,
    cardEnabled: data?.cardEnabled ?? true,
    creditTermsEnabled: data?.creditTermsEnabled ?? true,
    installmentsEnabled: data?.installmentsEnabled ?? true,
    promptPaymentDiscountEnabled: data?.promptPaymentDiscountEnabled ?? false,
    promptPaymentDiscountPercentage: data?.promptPaymentDiscountPercentage ?? 2,
    promptPaymentDiscountMethods: data?.promptPaymentDiscountMethods ?? ["multibanco", "mbway"],
    isLoading,
  };
}
